/**
 * T Stage and Surgical Margin Fields
 *
 * This component is used to display the T Stage and Surgical Margin fields.
 * It is used in the various prostate post-rp forms.
 *
 * @param control - The control object from react-hook-form
 * @param errors - The errors object from react-hook-form
 * @returns The T Stage and Surgical Margin fields component
 */
import { Controller, Path } from 'react-hook-form';
import { Box } from '@mui/material';
import z from 'zod';
import { SURGICAL_MARGIN_OPTIONS } from '@samd/features/orders';
import { RadioGroupField } from 'src/components/radio_group';
import {
  FormFieldProps,
  getProstatePostRpTStageOptions,
  getProstatePostRpTStages,
} from 'src/distribution_platforms/features/order_form';
import { SurgicalMargin } from 'src/types';

export const TStageAndSurgicalMarginFieldsSchema = z.object({
  tStage: z.enum(getProstatePostRpTStages(), {
    error: 'T Stage is required',
  }),
  surgicalMargin: z.enum(Object.values(SurgicalMargin), {
    error: 'Surgical Margin is required',
  }),
});

export type TStageAndSurgicalMarginFieldsValues = z.infer<
  typeof TStageAndSurgicalMarginFieldsSchema
>;

export const TStageAndSurgicalMarginFields = <
  T extends TStageAndSurgicalMarginFieldsValues,
>({
  control,
  errors,
  trigger,
}: FormFieldProps<T>) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: { xs: 2, sm: 3 },
    }}
  >
    <Box
      sx={{
        flex: 1,
      }}
    >
      <Controller
        name={'tStage' as Path<T>}
        control={control}
        render={({ field }) => (
          <RadioGroupField
            row
            required
            name={field.name}
            label='Pathologic T-stage'
            value={field.value}
            onBlur={field.onBlur}
            options={getProstatePostRpTStageOptions()}
            validationText={errors.tStage?.message?.toString() ?? ''}
            onChange={async (event) => {
              field.onChange(event);
            }}
          />
        )}
      />
    </Box>
    <Box
      sx={{
        flex: 1,
      }}
    >
      <Controller
        name={'surgicalMargin' as Path<T>}
        control={control}
        render={({ field }) => (
          <RadioGroupField
            row
            required
            name={field.name}
            label='Surgical Margin'
            value={field.value}
            onBlur={field.onBlur}
            options={SURGICAL_MARGIN_OPTIONS}
            validationText={errors.surgicalMargin?.message?.toString() ?? ''}
            onChange={async (event) => {
              field.onChange(event);
              trigger?.('surgicalMargin' as Path<T>);
            }}
          />
        )}
      />
    </Box>
  </Box>
);
